/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  MutationKey,
  useMutation,
  UseMutationResult,
} from "@tanstack/react-query";
import { GramCore } from "../core.js";
import { toolsetsUpdateExternalOAuthServer } from "../funcs/toolsetsUpdateExternalOAuthServer.js";
import { combineSignals } from "../lib/primitives.js";
import { RequestOptions } from "../lib/sdks.js";
import { Toolset } from "../models/components/toolset.js";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  UpdateExternalOAuthServerRequest,
  UpdateExternalOAuthServerSecurity,
} from "../models/operations/updateexternaloauthserver.js";
import { unwrapAsync } from "../types/fp.js";
import { useGramContext } from "./_context.js";
import { MutationHookOptions } from "./_types.js";

export type UpdateExternalOAuthServerMutationVariables = {
  request: UpdateExternalOAuthServerRequest;
  security?: UpdateExternalOAuthServerSecurity | undefined;
  options?: RequestOptions;
};

export type UpdateExternalOAuthServerMutationData = Toolset;

export type UpdateExternalOAuthServerMutationError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * updateExternalOAuthServer toolsets
 *
 * @remarks
 * Change an attached external OAuth server between provider-hosted and Speakeasy-hosted authorization-server metadata without replacing the server, registrations, tokens, or toolset association
 */
export function useUpdateExternalOAuthServerMutation(
  options?: MutationHookOptions<
    UpdateExternalOAuthServerMutationData,
    UpdateExternalOAuthServerMutationError,
    UpdateExternalOAuthServerMutationVariables
  >,
): UseMutationResult<
  UpdateExternalOAuthServerMutationData,
  UpdateExternalOAuthServerMutationError,
  UpdateExternalOAuthServerMutationVariables
> {
  const client = useGramContext();
  return useMutation({
    ...buildUpdateExternalOAuthServerMutation(client, options),
    ...options,
  });
}

export function mutationKeyUpdateExternalOAuthServer(): MutationKey {
  return ["@gram/client", "toolsets", "updateExternalOAuthServer"];
}

export function buildUpdateExternalOAuthServerMutation(
  client$: GramCore,
  hookOptions?: RequestOptions,
): {
  mutationKey: MutationKey;
  mutationFn: (
    variables: UpdateExternalOAuthServerMutationVariables,
  ) => Promise<UpdateExternalOAuthServerMutationData>;
} {
  return {
    mutationKey: mutationKeyUpdateExternalOAuthServer(),
    mutationFn: function updateExternalOAuthServerMutationFn({
      request,
      security,
      options,
    }): Promise<UpdateExternalOAuthServerMutationData> {
      const mergedOptions = {
        ...hookOptions,
        ...options,
        fetchOptions: {
          ...hookOptions?.fetchOptions,
          ...options?.fetchOptions,
          signal: combineSignals(
            hookOptions?.fetchOptions?.signal,
            options?.fetchOptions?.signal,
          ),
        },
      };
      return unwrapAsync(toolsetsUpdateExternalOAuthServer(
        client$,
        request,
        security,
        mergedOptions,
      ));
    },
  };
}
